import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { config } from "@/config";
import {
  fetchSystemSettings,
  updateSystemSettings,
  type SystemSettings,
} from "@/features/admin/api/system-settings.api";
import {
  canonicalClientPath,
  canonicalCompetitorsPath,
  DEFAULT_CLIENT_PATH,
  DEFAULT_COMPETITORS_PATH,
} from "@/lib/creative-asset-urls";
import { queryClient } from "@/lib/query-client";

const SYSTEM_SETTINGS_STALE_TIME_MS = 5 * 60 * 1000;

export const systemSettingsKeys = {
  admin: ["admin", "system-settings"] as const,
  public: ["public", "system-settings"] as const,
} as const;

export function systemSettingsQueryOptions() {
  return queryOptions({
    queryKey: systemSettingsKeys.admin,
    queryFn: fetchSystemSettings,
    staleTime: SYSTEM_SETTINGS_STALE_TIME_MS,
  });
}

export interface PublicCreativeUrlSettings {
  clientPath: string;
  competitorsPath: string;
}

const DEFAULT_PUBLIC_CREATIVE_URL_SETTINGS: PublicCreativeUrlSettings = {
  clientPath: DEFAULT_CLIENT_PATH,
  competitorsPath: DEFAULT_COMPETITORS_PATH,
};

function toPublicCreativeUrlSettings(value: unknown): PublicCreativeUrlSettings {
  if (value === null || typeof value !== "object") return DEFAULT_PUBLIC_CREATIVE_URL_SETTINGS;
  const record = value as { clientPath?: unknown; competitorsPath?: unknown };
  return {
    clientPath: canonicalClientPath(record.clientPath) ?? DEFAULT_CLIENT_PATH,
    competitorsPath: canonicalCompetitorsPath(record.competitorsPath) ?? DEFAULT_COMPETITORS_PATH,
  };
}

async function fetchPublicCreativeUrlSettings(): Promise<PublicCreativeUrlSettings> {
  try {
    const response = await fetch(`${config.apiUrl}/public/system-settings`);
    if (!response.ok) return DEFAULT_PUBLIC_CREATIVE_URL_SETTINGS;
    return toPublicCreativeUrlSettings(await response.json());
  } catch {
    return DEFAULT_PUBLIC_CREATIVE_URL_SETTINGS;
  }
}

/** Prefetched on the authenticated layout so URL builders have a value at first paint. */
export function creativeUrlSettingsQueryOptions() {
  return queryOptions({
    queryKey: systemSettingsKeys.public,
    queryFn: fetchPublicCreativeUrlSettings,
    staleTime: SYSTEM_SETTINGS_STALE_TIME_MS,
  });
}

export function useCreativeUrlSettings(): PublicCreativeUrlSettings {
  const query = useQuery(creativeUrlSettingsQueryOptions());
  return toPublicCreativeUrlSettings(query.data ?? DEFAULT_PUBLIC_CREATIVE_URL_SETTINGS);
}

/** For render callbacks that cannot call a hook. Falls back to the shipping paths. */
export function readCachedCreativeUrlSettings(): PublicCreativeUrlSettings {
  return toPublicCreativeUrlSettings(queryClient.getQueryData(systemSettingsKeys.public));
}

export function useSaveSystemSettings() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: {
      clientPath: string;
      competitorsPath: string;
      expectedRevision: number;
    }) => updateSystemSettings(input),
    onSuccess: (settings: SystemSettings) => {
      client.setQueryData(systemSettingsKeys.admin, settings);
      client.setQueryData(systemSettingsKeys.public, {
        clientPath: settings.clientPath,
        competitorsPath: settings.competitorsPath,
      });
    },
    meta: { skipGlobalError: true },
  });
}
